import type { LegendItem } from '@/utils/layerStyle';
import { legendParams } from '@/utils/platformViews';
import { StyleSwatch } from '@/components/Layers/StyleSwatch';
import { layerLabel } from '@/utils/layerLabel';
import { currentLanguage } from '@/i18n/format';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronUp, GripVertical } from 'lucide-react';
import { useLayers } from '../../hooks/useLayers';
import { useMapStore } from '../../store/map.store';
import { cn } from '../../utils/cn';
import ImageWMS from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
import { getMapColors } from '../../themes/mapTheme';

/* ── Legend entry types ── */
interface WmsLegendEntry {
  kind: 'wms';
  name: string;
  /** The layer's name in the current language. */
  label: string;
  legendUrl: string;
}

interface VectorLegendEntry {
  kind: 'vector';
  name: string;
  fillColor: string;
  strokeColor: string;
}

interface CompanyLegendEntry {
  kind: 'company';
  name: string;
  label: string;
  items: LegendItem[];
}

type LegendEntry = WmsLegendEntry | VectorLegendEntry | CompanyLegendEntry;

/* ── Helpers ── */

/** Extract a CSS-friendly colour string from an OL Fill/Stroke colour value */
function colorToString(c: any): string {
  if (!c) return 'transparent';
  if (typeof c === 'string') return c;
  if (Array.isArray(c)) {
    const [r, g, b, a] = c;
    return a !== undefined ? `rgba(${r},${g},${b},${a})` : `rgb(${r},${g},${b})`;
  }
  return 'transparent';
}

/** Human-readable label from internal layer names */
function friendlyName(raw: string): string {
  // catchmentRenderer layers: "1stDriveLayer" → "1. Drive"
  const catchMatch = raw.match(/^(\d+)(?:st|nd|rd|th)(Walkin|Drive|Motor)Layer$/i);
  if (catchMatch) return `${catchMatch[1]}. ${i18n.t(`legend.mode_${catchMatch[2].toLowerCase()}`)}`;
  if (raw === 'RingBuffer') return i18n.t('legend.ring_buffer');
  if (raw === 'dispConfLayer') return i18n.t('legend.selection');
  // MapView config layers: "walk1" → "Walk 1"
  const confMatch = raw.match(/^(walk|drive|motor)(\d+)$/i);
  if (confMatch) return `${i18n.t(`legend.mode_${confMatch[1].toLowerCase() === 'walk' ? 'walkin' : confMatch[1].toLowerCase()}`)} ${confMatch[2]}`;
  return raw;
}

/** Layers to always exclude from the legend */
const HIDDEN_LAYERS = new Set([
  'base-road', 'base-satellite', 'base-hybrid-labels', 'base-terrain', 'base-gray',
  'wms-layer', 'drawLayer', 'measureLayer', 'editLayer',
]);

export interface MapLegendProps {
  // no longer needs external props — reads everything from the map
}

export const MapLegend: React.FC<MapLegendProps> = () => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { layers: dbLayers, visibility, getLayerLegendUrl } = useLayers();
  const mapInstance = useMapStore((s) => s.mapInstance);

  /* Force closed on every mount — defeats HMR state preservation */
  useEffect(() => {
    setExpanded(false);
  }, []);

  /* ── Dragging ── */
  const [offset, setOffset] = useState({ dx: 0, dy: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, dx: 0, dy: 0 });

  const onDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, dx: offset.dx, dy: offset.dy };
  }, [offset]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      setOffset({
        dx: dragStart.current.dx + (e.clientX - dragStart.current.mouseX),
        dy: dragStart.current.dy + (e.clientY - dragStart.current.mouseY),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  /* ── Build legend entries from the actual OL map layer collection ── */
  const [entries, setEntries] = useState<LegendEntry[]>([]);
  const [tick, setTick] = useState(0); // force re-scan

  // Scan the map every time tick changes (triggered by layer events)
  useEffect(() => {
    if (!mapInstance) { setEntries([]); return; }

    const result: LegendEntry[] = [];

    mapInstance.getLayers().forEach((layer: any) => {
      if (!layer.getVisible()) return;
      const name: string = layer.get?.('name') || '';
      if (!name || HIDDEN_LAYERS.has(name)) return;

      // A company's own layer: its legend is made from its style (utils/layerStyle.ts).
      if (layer.get?.('companyLayer')) {
        result.push({ kind: 'company', name, label: layer.get('legendTitle') ?? name, items: layer.get('legendItems') ?? [] });
        return;
      }

      const src = layer.getSource?.();

      // WMS layer (ImageWMS or TileWMS)
      if (src && (src instanceof ImageWMS || src instanceof TileWMS)) {
        // Try to find matching DB layer for a proper legend URL
        const dbLayer = dbLayers.find(
          (l) => (l.displayName || l.layerName) === name || l.layerName === name,
        );
        // the legend of the look the map shows: the person's own view, or a look being tried (GXV, GXPREVIEW)
        const legendUrl = (dbLayer
          ? getLayerLegendUrl(dbLayer)
          : buildWmsLegendUrl(src, name)) + legendParams(src);
        result.push({ kind: 'wms', name, label: dbLayer ? layerLabel(dbLayer) : name, legendUrl });
        return;
      }

      // Vector layer with features (duck-type check: source has getFeatures)
      if (src && typeof src.getFeatures === 'function') {
        const features = src.getFeatures();
        if (!features || features.length === 0) return;

        const style = layer.getStyle?.();
        const mc = getMapColors();
        let fillColor = 'rgba(48,127,175,0.25)';
        let strokeColor = mc.selection;

        if (style && typeof style.getFill === 'function') {
          const fill = style.getFill?.();
          const stroke = style.getStroke?.();
          if (fill) fillColor = colorToString(fill.getColor());
          if (stroke) strokeColor = colorToString(stroke.getColor());
        } else if (typeof style === 'function') {
          // Style may be a function — try calling with null to get the default style
          try {
            const resolved = (style as any)(null, 1);
            const s = Array.isArray(resolved) ? resolved[0] : resolved;
            if (s && typeof s.getFill === 'function') {
              const fill = s.getFill?.();
              const stroke = s.getStroke?.();
              if (fill) fillColor = colorToString(fill.getColor());
              if (stroke) strokeColor = colorToString(stroke.getColor());
            }
          } catch { /* ignore */ }
        }

        result.push({ kind: 'vector', name: friendlyName(name), fillColor, strokeColor });
      }
    });

    setEntries(result);
  }, [mapInstance, tick, dbLayers, visibility, getLayerLegendUrl, i18n.language]);

  // Listen for layer add/remove/visibility changes on the map
  useEffect(() => {
    if (!mapInstance) return;
    const bump = () => setTick((t) => t + 1);

    // Fires when layers are added or removed
    const layers = mapInstance.getLayers();
    layers.on('add', bump);
    layers.on('remove', bump);

    // Fires on moveend (covers visibility changes after rendering)
    mapInstance.on('moveend', bump);

    // Also poll periodically to catch programmatic visibility/feature changes
    const interval = setInterval(bump, 1000);

    return () => {
      layers.un('add', bump);
      layers.un('remove', bump);
      mapInstance.un('moveend', bump);
      clearInterval(interval);
    };
  }, [mapInstance]);

  const glassStyle: React.CSSProperties = {
    background: 'var(--gn-glass-bg)',
    backdropFilter: 'blur(10px)',
    border: '1px solid var(--gn-glass-border)',
  };

  const wmsEntries = entries.filter((e): e is WmsLegendEntry => e.kind === 'wms');
  const vectorEntries = entries.filter((e): e is VectorLegendEntry => e.kind === 'vector');
  const companyEntries = entries.filter((e): e is CompanyLegendEntry => e.kind === 'company');

  return (
    <div
      className="fixed z-40"
      style={{
        right: 24 - offset.dx,
        bottom: 56 - offset.dy,
        width: 220,
        userSelect: dragging ? 'none' : undefined,
      }}
      role="region"
      aria-label={t('legend.aria')}
    >
      <div className="relative">

        {/* ── Content panel: positioned ABOVE the header bar ── */}
        {expanded && (
          <div
            className="absolute left-0 right-0 overflow-hidden"
            style={{
              ...glassStyle,
              bottom: '100%',
              marginBottom: -1,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottom: 'none',
            }}
          >
            <div className="p-3 space-y-2 max-h-[50vh] overflow-y-auto">

              {/* ── Vector / analysis layers ── */}
              {vectorEntries.length > 0 && (
                <div className="pb-2 border-b border-gn-light">
                  <h3 className="text-[10px] font-semibold text-gn-blue mb-1.5 uppercase">
                    {t('legend.analysis_layers')}
                  </h3>
                  <div className="space-y-1">
                    {vectorEntries.map((entry, idx) => (
                      <div key={`${entry.name}-${idx}`} className="flex items-center gap-2">
                        <div
                          className="w-3.5 h-3.5 rounded"
                          style={{
                            backgroundColor: entry.fillColor,
                            border: `2px solid ${entry.strokeColor}`,
                          }}
                        />
                        <span className="text-[10px] text-gn-primary">{entry.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── The company's own layers ── */}
              {companyEntries.length > 0 && (
                <div className="pb-2 border-b border-gn-light">
                  <h3 className="text-[10px] font-semibold text-gn-blue mb-1.5 uppercase">{t('styles.legend_own')}</h3>
                  <div className="space-y-2">
                    {companyEntries.map((entry, idx) => (
                      <div key={`${entry.name}-${idx}`} className="text-[10px]">
                        <p className="text-gn-secondary mb-0.5 font-medium">{entry.label}</p>
                        <div className="space-y-0.5">
                          {entry.items.map((it, j) => it.kind === 'ramp' ? (
                            <div key={j} className="flex items-center gap-1">
                              <span className="text-gn-muted">{t('styles.legend_low')}</span>
                              <span className="flex-1 h-2 rounded" style={{ background: `linear-gradient(90deg, ${it.colours.join(', ')})` }} />
                              <span className="text-gn-muted">{t('styles.legend_high')}</span>
                            </div>
                          ) : (
                            <div key={j} className="flex items-center gap-2">
                              <span className="inline-flex justify-center" style={{ minWidth: 28 }}><StyleSwatch symbol={it} /></span>
                              <span className="text-gn-primary truncate">{it.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── WMS layers ── */}
              {wmsEntries.length > 0 ? (
                <div>
                  <h3 className="text-[10px] font-semibold text-gn-primary mb-1.5 uppercase">{t('map.layers')}</h3>
                  <div className="space-y-1.5">
                    {wmsEntries.map((entry, idx) => (
                      <div key={`${entry.name}-${idx}`} className="text-[10px]">
                        <p className="text-gn-secondary mb-0.5 font-medium">{entry.label}</p>
                        <img
                          src={entry.legendUrl}
                          alt={t('legend.alt', { name: entry.label })}
                          className="max-w-full h-auto rounded border border-gn"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : vectorEntries.length === 0 && companyEntries.length === 0 ? (
                <p className="text-[10px] text-gn-secondary italic">{t('legend.no_visible')}</p>
              ) : null}
            </div>
          </div>
        )}

        {/* ── Header bar: always visible ── */}
        <div
          onMouseDown={onDragStart}
          className={cn(
            'flex items-center justify-between px-3 py-2 text-xs font-semibold text-gn-primary shadow-lg',
            expanded ? 'rounded-b-lg' : 'rounded-lg',
          )}
          style={{
            ...glassStyle,
            cursor: dragging ? 'grabbing' : 'grab',
            ...(expanded ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : {}),
          }}
        >
          <span className="flex items-center gap-1.5">
            <GripVertical size={11} className="text-gn-secondary" />
            {t('map.legend')}
            {entries.length > 0 && (
              <span className="text-[9px] text-gn-secondary font-normal">({entries.length})</span>
            )}
          </span>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-0.5 rounded hover:bg-gn-tertiary text-gn-secondary hover:text-gn-primary transition-colors"
            aria-expanded={expanded}
          >
            <ChevronUp
              size={14}
              className={cn('transition-transform duration-200', expanded ? '' : 'rotate-180')}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Fallback: build a GetLegendGraphic URL from the WMS source itself ── */
function buildWmsLegendUrl(src: ImageWMS | TileWMS, name: string): string {
  const baseUrl = (src as any).getUrls?.()[0] || (src as any).getUrl?.() || '';
  const params = (src as any).getParams?.() || {};
  const layerParam = params.LAYERS || name;
  if (!baseUrl) return '';
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}SERVICE=WMS&VERSION=1.1.1&REQUEST=GetLegendGraphic&LAYER=${layerParam}&FORMAT=image/png&SCALE=10000&LANGUAGE=${currentLanguage()}`;
}
